import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Check, Clock, XCircle, MessageCircle, Home, Loader2, FileText } from "lucide-react";
import { useSeoMeta } from "../i18n/LanguageContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const WHATSAPP_NUMBER = "201037255878";

export default function PaymentResultPage() {
  const [params] = useSearchParams();
  const clientRef = params.get("ref");
  const [state, setState] = useState({ loading: true, invoice: null, error: "" });
  useSeoMeta({
    title: "Esito Pagamento | Locker Room",
    description: "Controlla l'esito del pagamento del tuo ordine Locker Room.",
    noindex: true,
  });

  useEffect(() => {
    if (!clientRef) {
      setState({ loading: false, invoice: null, error: "Riferimento pagamento mancante" });
      return;
    }
    let mounted = true;
    let attempts = 0;

    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payment/status/${clientRef}`);
        if (!mounted) return;
        setState({ loading: false, invoice: data, error: "" });
        // Poll for up to 30s if still PENDING/CREATED (webhook race)
        if ((data.status === "CREATED" || data.status === "PENDING" || data.status === "INITIATED") && attempts < 10) {
          attempts += 1;
          setTimeout(poll, 3000);
        }
      } catch (e) {
        if (!mounted) return;
        setState({ loading: false, invoice: null, error: "Impossibile recuperare lo stato del pagamento" });
      }
    };
    poll();
    return () => { mounted = false; };
  }, [clientRef]);

  const openWhatsApp = () => {
    const inv = state.invoice;
    const lines = [
      `Ciao Locker Room,`,
      "",
      `ho appena completato il pagamento dell'ordine:`,
      `Riferimento: ${inv?.client_ref || "-"}`,
      `Importo: €${inv?.amount || "-"}`,
      `Descrizione: ${inv?.description || "-"}`,
      "",
      `Conferma pagamento come da ricevuta Tap. Grazie!`,
    ];
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(lines.join("\n"))}`;
    window.open(url, "_blank");
  };

  const status = state.invoice?.status;
  const isPaid = status === "PAID" || status === "CAPTURED";
  const isPending = status === "CREATED" || status === "PENDING" || status === "INITIATED";
  const isFailed = status === "FAILED" || status === "DECLINED" || status === "CANCELLED";

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        {/* Header */}
        <div className="text-center mb-10">
          <img src="/lr-monogram.svg" alt="Locker Room – boutique premium di maglie sportive" className="h-20 w-auto mx-auto mb-4" />
          <div className="font-heading text-2xl md:text-3xl font-bold tracking-[0.15em]">
            <span className="text-[#009A49]">LOCKER</span>{" "}
            <span className="text-white">ROOM</span>
          </div>
        </div>

        <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-8 md:p-10 text-center" data-testid="result-card">
          {state.loading && (
            <>
              <Loader2 className="w-14 h-14 text-[#009A49] mx-auto mb-6 animate-spin" />
              <h1 className="font-heading text-2xl font-bold text-white mb-3">Verifica in corso…</h1>
              <p className="text-zinc-400 font-body text-sm">Stiamo controllando lo stato del tuo pagamento.</p>
            </>
          )}

          {!state.loading && state.error && (
            <>
              <XCircle className="w-14 h-14 text-red-500 mx-auto mb-6" />
              <h1 className="font-heading text-2xl font-bold text-white mb-3">Errore</h1>
              <p className="text-zinc-400 font-body text-sm mb-6">{state.error}</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 text-white font-body font-semibold text-xs tracking-[0.2em] uppercase px-6 py-3 rounded-full transition-colors border border-white/10"
              >
                <Home className="w-4 h-4" />
                Torna al sito
              </Link>
            </>
          )}

          {!state.loading && isPaid && (
            <>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, type: "spring" }}
                className="w-20 h-20 rounded-full bg-[#009A49] flex items-center justify-center mx-auto mb-6"
              >
                <Check className="w-10 h-10 text-white" />
              </motion.div>
              <h1 className="font-heading text-3xl font-bold text-white mb-3" data-testid="payment-success-title">Pagamento Completato</h1>
              <p className="text-zinc-400 font-body text-sm mb-6">
                Grazie! Il tuo ordine <span className="text-[#009A49] font-mono">{state.invoice.client_ref}</span> di <span className="text-white font-semibold">€{state.invoice.amount}</span> è confermato.
              </p>
              <button
                onClick={openWhatsApp}
                className="w-full bg-[#25D366] hover:bg-[#20b757] text-white font-body font-bold text-sm tracking-[0.2em] uppercase py-4 rounded-full transition-colors flex items-center justify-center gap-3 mb-3"
                data-testid="whatsapp-notify-btn"
              >
                <MessageCircle className="w-5 h-5" />
                Conferma su WhatsApp
              </button>
              <a
                href={`${API}/payment/receipt/${clientRef}.pdf`}
                target="_blank"
                rel="noreferrer"
                className="w-full bg-white/5 hover:bg-white/10 text-white font-body font-semibold text-xs tracking-[0.2em] uppercase py-3 rounded-full transition-colors border border-white/10 flex items-center justify-center gap-3 mb-3"
                data-testid="download-receipt-btn"
              >
                <FileText className="w-4 h-4" />
                Scarica Ricevuta PDF
              </a>
              <Link
                to="/"
                className="block text-zinc-500 hover:text-white text-xs tracking-[0.2em] uppercase font-body py-2"
              >
                Torna al sito
              </Link>
            </>
          )}

          {!state.loading && isPending && (
            <>
              <Clock className="w-14 h-14 text-[#009A49] mx-auto mb-6" />
              <h1 className="font-heading text-2xl font-bold text-white mb-3">In elaborazione</h1>
              <p className="text-zinc-400 font-body text-sm mb-6">
                Il pagamento è in fase di conferma. Se hai completato la transazione, riceverai la ricevuta a breve.
              </p>
              <button
                onClick={openWhatsApp}
                className="w-full bg-[#25D366] hover:bg-[#20b757] text-white font-body font-bold text-sm tracking-[0.2em] uppercase py-4 rounded-full transition-colors flex items-center justify-center gap-3 mb-3"
              >
                <MessageCircle className="w-5 h-5" />
                Contatta il Negozio
              </button>
              <Link
                to="/"
                className="block text-zinc-500 hover:text-white text-xs tracking-[0.2em] uppercase font-body py-2"
              >
                Torna al sito
              </Link>
            </>
          )}

          {!state.loading && isFailed && (
            <>
              <XCircle className="w-14 h-14 text-red-500 mx-auto mb-6" />
              <h1 className="font-heading text-2xl font-bold text-white mb-3">Pagamento non riuscito</h1>
              <p className="text-zinc-400 font-body text-sm mb-6">
                Il pagamento non è stato completato. Contatta il negozio o riprova.
              </p>
              <button
                onClick={openWhatsApp}
                className="w-full bg-[#25D366] hover:bg-[#20b757] text-white font-body font-bold text-sm tracking-[0.2em] uppercase py-4 rounded-full transition-colors flex items-center justify-center gap-3 mb-3"
              >
                <MessageCircle className="w-5 h-5" />
                Contatta il Negozio
              </button>
              <Link
                to="/"
                className="block text-zinc-500 hover:text-white text-xs tracking-[0.2em] uppercase font-body py-2"
              >
                Torna al sito
              </Link>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
